import * as vp from 'vopay';
import { Box, Title, Toast, Button, Tooltip, Icon, CardTabs } from 'vopay';
import { FunctionComponent, useState, useEffect } from 'react';
import {
  format,
  subMonths,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  subQuarters,
  startOfQuarter,
  endOfQuarter,
  subDays,
  startOfWeek,
  endOfWeek,
  compareAsc,
  parseISO,
} from 'date-fns';
import AccountingSoftware from '../../Intergrations/AccountingSoftware/AccountingSoftware';
import AccountingSystemSearchForm from './AccountingSystemSearchForm';
import AccountingSystemClosed from './AccountingSystemClosed/AccountingSystemClosed';
import AccountingSystemOpen from './AccountingSystemOpen/AccountingSystemOpen';
import useFetchRailzBusinessAccount from '@/hooks/useApi/useFetchRailzBusinessAccount';
import useFetchRailzBills from '@/hooks/useApi/useFetchRailzBills';
import useFetchRailzInvoices from '@/hooks/useApi/useFetchRailzInvoices';
import useFetchRailzAccount from '@/hooks/useApi/useFetchRailzAccount';
import usePostAccountBalance from '@/hooks/useApi/usePostAccountBalance';
import usePostRailzSync from '@/hooks/useApi/usePostRailzSync';
import usePostRailzSyncData from '@/hooks/useApi/useFetchRailzSyncStatus';
import AccountingSystemDialogue from './AccountingSystemDialogue/AccountingSystemDialogue';
import AccountingSystemContext, { AccountingSystemCtx } from './AccountingSystemDialogue/AccountingSystemContext';
import { AccountingSystemDialogueHandler, DialogueHandlerProps } from './AccountingSystemDialogue/types';
import { useClientLogin } from '@/providers/ClientLogin.provider';
import styles from './AccountingSystem.module.scss';
import AvailableBalance from '../../UI/AvailableBalance/AvailableBalance';
import useDataFetchIndicator from '@/hooks/useDataFetchIndicator';
import { Trans, useTranslation } from 'react-i18next';
import { TableFiltersContainer } from 'UI';
type AccountingType = 'Payable' | 'Receivable';
type Tab = 'open' | 'closed';

type AccountingSystemContainerProps = {
  Type: AccountingType;
};

type FC = FunctionComponent<AccountingSystemContainerProps>;
const AccountingSystemContainer: FC = ({ Type }: AccountingSystemContainerProps) => {
  const [, , fetchRailzBills] = useFetchRailzBills({});
  const [, , fetchRailzInvoices] = useFetchRailzInvoices({});
  const [railzBusinessAccountResponse, fetchRailzBusinessAccount, resetRailzBusinessAccount] =
    useFetchRailzBusinessAccount({});
  const [, , fetchChartOfAccount] = useFetchRailzAccount({});
  const [, setDataFetchingInfo] = useDataFetchIndicator();
  const [balanceResponse, fetchBalance] = usePostAccountBalance({});
  const [syncDataResponse, syncData, resetSyncData] = usePostRailzSync({});
  const [syncStatusResponse, fetchSyncStatus] = usePostRailzSyncData({});
  const [fetchStatus, setFetchStatus] = useState(true);
  const [isSyncingData, setIsSyncingData] = useState(false);
  const [showConnect, setShowConnect] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [lastSync, setLastSync] = useState('');
  const [businessAccount, setBusinessAccount] = useState<vp.RailzBusinessAccount | undefined>(undefined);
  const [railzBillsOrInvoices, setRailzBillsOrInvoices] = useState<vp.BillOrInvoiceRailz[]>([]);
  const [BillsOrInvoices, setBillsOrInvoices] = useState<vp.BillOrInvoiceRailz[]>([]);
  const [railzBillsOrInvoicesFiltered, setRailzBillsOrInvoicesFiltered] = useState<vp.BillOrInvoiceRailz[]>([]);
  const [BillsOrInvoicesFiltered, setBillsOrInvoicesFiltered] = useState<vp.BillOrInvoiceRailz[]>([]);

  const [showChartOfAccountSuccessfull, setShowChartOfAccountSuccessfull] = useState(false);

  const [chartOfAccountNameRecievable, setChartOfAccountNameRecievable] = useState('');
  const [chartOfAccountNamePayable, setChartOfAccountNamePayable] = useState('');
  const { response } = useClientLogin();
  const [startDate, setStartDate] = useState<vp.Timestamp | undefined>(
    startOfMonth(subMonths(new Date(), 1)).getTime() as vp.Timestamp,
  );
  const [endDate, setEndDate] = useState<vp.Timestamp | undefined>(endOfMonth(new Date()).getTime() as vp.Timestamp);
  const transactionsPeriod = {
    FromDate: startDate,
    ToDate: endDate,
  };

  const [activeTab, setActiveTab] = useState<Tab>('open');

  const [type, setType] = useState('');
  const [currency, setCurrency] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const [timeRange, setTimeRange] = useState('custom');

  const [lastStartDate, setLastStartDate] = useState<vp.Timestamp | undefined>(startDate);
  const [lastEndDate, setLastEndDate] = useState<vp.Timestamp | undefined>(endDate);

  const [showSyncStarted, setShowSyncStarted] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { t } = useTranslation('translations', { keyPrefix: 'AccountingSystem' });

  const path = Type === 'Payable' ? 'payables' : 'receivables';

  const titleMap: Record<AccountingType, string> = {
    Payable: t('pageTitlePayable'),
    Receivable: t('pageTitleReceivable'),
  };

  const accountingSystemCtx: AccountingSystemCtx = {
    showSyncStarted,
    showError,
    errorMessage,
  };

  const handlers = {
    handleAccountDialogue: (handlerProps: DialogueHandlerProps<AccountingSystemDialogueHandler>) => {
      const { type: typeHandler, state: state2, action = 'default' } = handlerProps;

      const dialogueHandlers: AccountingSystemDialogueHandler = {
        error: {
          confirm: {
            default: () => {
              setShowError(false);
            },
          },
        },
        sync: {
          confirm: {
            default: () => {
              setShowSyncStarted(false);
            },
          },
          fail: {
            default: () => {},
          },
        },
      };
      const dialogueState = dialogueHandlers[typeHandler][state2];
      const handler = dialogueState ? dialogueState[action] || dialogueState.default : () => {};

      handler();
    },
  };

  // Get the protocol, hostname, and port
  const { protocol, port, hostname } = window.location;
  let RedirectURL = `${protocol}//${hostname}/${path}/`;
  const currentPort = port || '3000';

  if (hostname === 'localhost' || hostname === 'bs-local.com') {
    RedirectURL = `${protocol}//${hostname}:${currentPort}/${path}/`;
  }

  const timeRangeChange = (timeRangeKey: string | undefined) => {
    if (timeRangeKey) {
      const today = new Date();
      let adjustedStartDate;
      let adjustedEndDate;

      switch (timeRangeKey) {
        case 'today':
          adjustedStartDate = today;
          adjustedEndDate = today;
          break;
        case 'yesterday':
          adjustedStartDate = subDays(today, 1);
          adjustedEndDate = adjustedStartDate;
          break;
        case 'thisWeek':
          adjustedStartDate = startOfWeek(today);
          adjustedEndDate = endOfWeek(today);
          break;
        case 'lastWeek':
          adjustedStartDate = startOfWeek(subDays(today, 7));
          adjustedEndDate = endOfWeek(subDays(today, 7));
          break;
        case 'thisMonth':
          adjustedStartDate = startOfMonth(today);
          adjustedEndDate = endOfMonth(today);
          break;
        case 'lastMonth':
          adjustedStartDate = startOfMonth(subMonths(today, 1));
          adjustedEndDate = endOfMonth(subMonths(today, 1));
          break;
        case 'thisQuarter':
          adjustedStartDate = startOfQuarter(today);
          adjustedEndDate = endOfQuarter(today);
          break;
        case 'lastQuarter':
          adjustedStartDate = startOfQuarter(subQuarters(today, 1));
          adjustedEndDate = endOfQuarter(subQuarters(today, 1));
          break;
        case 'last6Months':
          adjustedStartDate = startOfMonth(subMonths(today, 6));
          adjustedEndDate = endOfMonth(today);
          break;
        case 'last12Months':
          adjustedStartDate = undefined;
          adjustedEndDate = undefined;
          break;
        case 'custom':
          break;
        default:
          adjustedStartDate = startOfYear(new Date(today.setFullYear(Number(timeRangeKey.trim()))));
          adjustedEndDate = endOfYear(new Date(today.setFullYear(Number(timeRangeKey.trim()))));
      }

      setTimeRange(timeRangeKey);
      setStartDate(adjustedStartDate?.getTime() as vp.Timestamp);
      setEndDate(adjustedEndDate?.getTime() as vp.Timestamp);
    }
  };

  useEffect(() => {
    if (syncDataResponse) {
      resetSyncData();
      if (syncStatusResponse?.Success) {
        setShowSyncStarted(true);
      } else {
        setErrorMessage(syncStatusResponse?.ErrorMessage || '');
        setShowError(true);
      }
      setFetchStatus(true);
    }
  }, [syncDataResponse]);
  useEffect(() => {
    if (fetchStatus && businessAccount) {
      fetchSyncStatus({
        BusinessName: businessAccount.BusinessName || '',
        ServiceName: businessAccount.ServiceName || '',
      });
      setFetchStatus(false);
    }
  }, [fetchStatus, businessAccount]);
  useEffect(() => {
    if (syncStatusResponse) {
      if (syncStatusResponse.Success) {
        if (syncStatusResponse.SyncStatus === 'success') {
          setIsSyncingData(false);
        }
        syncStatusResponse.MostRecentUpdateDate &&
          setLastSync(format(parseISO(syncStatusResponse.MostRecentUpdateDate), 'yyyy-MM-dd HH:mm:ss'));
      }
    }
  }, [syncStatusResponse]);
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    if (isSyncingData) {
      timeoutId = setTimeout(() => {
        setFetchStatus(true);
      }, 60000);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isSyncingData]);

  useEffect(() => {
    fetchBalance({
      Currency: 'CAD',
    });
  }, []);
  const searchTransactions = () => {
    const keys = ['invoiceNumber', 'amountDue'];
    const keysInvoicesOrBills = ['InvoiceRefID', 'VIOAccountTransactions_ID', 'Amount'];

    setRailzBillsOrInvoicesFiltered(
      railzBillsOrInvoices
        .map((item) => item)
        .filter((data) => {
          const customerName: string = data.Customer?.Name || data.customerRef?.name || data.vendorRef?.name || '';
          const hasDetails = {
            hasBankAccount: !!(
              data.Customer &&
              data.Customer.BankAccount &&
              data.Customer.BankAccount.FinancialInstitutionNumber &&
              data.Customer.BankAccount.BranchTransitNumber &&
              data.Customer.BankAccount.AccountNumber &&
              ((response.Country === 'CA' &&
                ((Type === 'Receivable' && vp.toBool(response.EFTCollectEnabled)) ||
                  (Type === 'Payable' &&
                    (vp.toBool(response.EFTSendEnabled) || vp.toBool(response.RTRWithdrawEnabled))))) ||
                (response.Country === 'US' &&
                  ((Type === 'Receivable' && vp.toBool(response.ACHFundEnabled)) ||
                    (Type === 'Payable' &&
                      (vp.toBool(response.ACHWithdrawEnabled) || vp.toBool(response.RTPWithdrawEnabled))))))
            ),
            hasInterac: !!(
              data.Customer &&
              data.Customer.Interac &&
              (data.Customer.Interac.RecipientEmailAddress || data.Customer.Interac?.RecipientPhoneNumber) &&
              (data.Customer.Interac.Question || Type === 'Receivable') &&
              (data.Customer.Interac.Answer || Type === 'Receivable') &&
              ((response.Country === 'CA' && Type === 'Receivable' && vp.toBool(response.InteracMoneyRequestEnabled)) ||
                (response.Country === 'CA' && Type === 'Payable' && vp.toBool(response.InteracBulkPayoutEnabled)))
            ),
            hasDebitCard: !!(
              data.Customer &&
              data.Customer.DebitCard &&
              data.Customer.DebitCard.PushToCardToken &&
              vp.toBool(response.VisaDirectEnabled) &&
              Type === 'Payable'
            ),
            hasCreditCard: !!(
              data.Customer &&
              data.Customer.CreditCard &&
              data.Customer.CreditCard.CreditCardToken &&
              vp.toBool(response.CreditCardEnabled) &&
              Type === 'Receivable'
            ),
          };
          return (
            (currency === '' || currency.split('|').includes(data.currency)) &&
            (type === '' ||
              (hasDetails.hasBankAccount &&
                (type.split('|').includes('EFT') ||
                  type.split('|').includes('ACH') ||
                  type.split('|').includes('VoPay Instant'))) ||
              (hasDetails.hasInterac && type.split('|').includes('Interac e-Transfer')) ||
              (hasDetails.hasDebitCard && type.split('|').includes('Debit Card')) ||
              (hasDetails.hasCreditCard && type.split('|').includes('Credit Card'))) &&
            (searchTerm === '' ||
              keys.some((key) =>
                data[key as keyof vp.BillOrInvoiceRailz]?.toString().toLowerCase().includes(searchTerm.toLowerCase()),
              ) ||
              customerName.toLowerCase().includes(searchTerm.toLowerCase()))
          );
        })
        .sort((a, b) => compareAsc(parseISO(b.postedDate), parseISO(a.postedDate))),
    );

    setBillsOrInvoicesFiltered(
      BillsOrInvoices.map((item) => item)
        .filter((data) => {
          const customerName: string = data.Customer?.Name || data.customerRef?.name || data.vendorRef?.name || '';
          const paymentMethods =
            data.PaymentDetails?.map((payment) =>
              payment.TransactionAmount && Number(payment.TransactionAmount) > 0 ? payment.PaymentMethod : undefined,
            ).filter((payment) => !!payment) || [];
          const selectedTypes = type
            .split('|')
            .map((typeOriginal) =>
              typeOriginal === 'EFT' || typeOriginal === 'ACH' || typeOriginal === 'VoPay Instant'
                ? 'Bank Account'
                : typeOriginal === 'Interac e-Transfer'
                  ? 'Email'
                  : typeOriginal,
            );
          const hasMatchingPaymentMethod = paymentMethods.some((method) => method && selectedTypes.includes(method));
          return (
            (type === '' || hasMatchingPaymentMethod) &&
            (currency === '' || currency === 'all' || currency.split('|').includes(data.currency)) &&
            (searchTerm === '' ||
              keysInvoicesOrBills.some((key) =>
                data[key as keyof vp.BillOrInvoiceRailz]?.toString().toLowerCase().includes(searchTerm.toLowerCase()),
              ) ||
              customerName.toLowerCase().includes(searchTerm.toLowerCase()))
          );
        })
        .sort((a, b) => compareAsc(parseISO(b.postedDate), parseISO(a.postedDate))),
    );
  };

  useEffect(() => {
    setBillsOrInvoicesFiltered([...BillsOrInvoices]);
    searchTransactions();
  }, [BillsOrInvoices]);
  useEffect(() => {
    setRailzBillsOrInvoicesFiltered([...railzBillsOrInvoices]);
    searchTransactions();
  }, [railzBillsOrInvoices]);

  useEffect(() => {
    if (railzBusinessAccountResponse && railzBusinessAccountResponse.Success) {
      const hasNoAccount = Object.values(railzBusinessAccountResponse.Accounts).length === 0;
      if (!hasNoAccount) {
        const currentAccount: vp.RailzBusinessAccount | undefined = Object.values(
          railzBusinessAccountResponse.Accounts,
        )[0];
        setBusinessAccount(currentAccount);
      } else {
        setShowConnect(hasNoAccount);
      }

      if (hasNoAccount) {
        setIsInitialized(true);
      }
    }
  }, [railzBusinessAccountResponse]);
  const fetchBillsOrInvoices = async (accountingType: AccountingType, account: vp.RailzBusinessAccount) => {
    setDataFetchingInfo(true, 'loading some data', 'fullScreen');
    try {
      if (accountingType === 'Payable') {
        const [openBills, paidBills] = await Promise.all([
          fetchRailzBills({
            BusinessName: account.BusinessName || '',
            ServiceName: account.ServiceName || '',
            StartDate: format(new Date(Number(transactionsPeriod.FromDate)), 'yyyy-MM-dd'),
            EndDate: format(new Date(Number(transactionsPeriod.ToDate)), 'yyyy-MM-dd'),
            Status: 'open',
          }),
          fetchRailzBills({
            BusinessName: account.BusinessName || '',
            ServiceName: account.ServiceName || '',
            StartDate: format(new Date(Number(transactionsPeriod.FromDate)), 'yyyy-MM-dd'),
            EndDate: format(new Date(Number(transactionsPeriod.ToDate)), 'yyyy-MM-dd'),
            Status: 'paid',
          }),
        ]);
        if (openBills.Success && openBills.Bills) {
          setRailzBillsOrInvoices(openBills.Bills || []);
        } else {
          setRailzBillsOrInvoices([]);
        }
        if (paidBills.Success && paidBills.Bills) {
          setBillsOrInvoices(paidBills.Bills || []);
        } else {
          setBillsOrInvoices([]);
        }
      } else {
        const [openInvoices, paidInvoices] = await Promise.all([
          fetchRailzInvoices({
            BusinessName: account.BusinessName || '',
            ServiceName: account.ServiceName || '',
            StartDate: format(new Date(Number(transactionsPeriod.FromDate)), 'yyyy-MM-dd'),
            EndDate: format(new Date(Number(transactionsPeriod.ToDate)), 'yyyy-MM-dd'),

            Status: 'open',
          }),
          fetchRailzInvoices({
            BusinessName: account.BusinessName || '',
            ServiceName: account.ServiceName || '',
            StartDate: format(new Date(Number(transactionsPeriod.FromDate)), 'yyyy-MM-dd'),
            EndDate: format(new Date(Number(transactionsPeriod.ToDate)), 'yyyy-MM-dd'),
            Status: 'paid',
          }),
        ]);
        if (openInvoices.Success && openInvoices.Invoices) {
          setRailzBillsOrInvoices(openInvoices.Invoices || []);
        } else {
          setRailzBillsOrInvoices([]);
        }
        if (paidInvoices.Success && paidInvoices.Invoices) {
          setBillsOrInvoices(paidInvoices.Invoices || []);
        } else {
          setBillsOrInvoices([]);
        }
      }
    } finally {
      setDataFetchingInfo(false);
    }
  };
  useEffect(() => {
    const fetchCharts = async (BusinessName: string, ServiceName: string) => {
      const chartOfAccountRecievables = await fetchChartOfAccount({
        BusinessName,
        ServiceName,
        Type: 'receivables',
      });
      const chartOfAccountPayables = await fetchChartOfAccount({
        BusinessName,
        ServiceName,
        Type: 'payables',
      });
      if (
        chartOfAccountRecievables.Success &&
        chartOfAccountRecievables.Account &&
        chartOfAccountPayables.Success &&
        chartOfAccountPayables.Account
      ) {
        setShowConnect(false);
      } else {
        setShowConnect(true);
      }
    };

    if (businessAccount) {
      fetchCharts(businessAccount.BusinessName || '', businessAccount.ServiceName || '');
      fetchBillsOrInvoices(Type, businessAccount);
      setIsInitialized(true);
    }
  }, [businessAccount]);

  const OnSuccess = () => {
    if (businessAccount) {
      fetchBillsOrInvoices(Type, businessAccount);
    }
  };

  useEffect(() => {
    setIsInitialized(false);
    setActiveTab('open');
    setRailzBillsOrInvoices([]);
    setBillsOrInvoices([]);
    resetRailzBusinessAccount();
    fetchRailzBusinessAccount({});
    setFetchStatus(true);
  }, [Type]);
  const onCompleteChartOfAccount = (accountNameRecievable: string, accountNamePayable: string) => {
    setChartOfAccountNameRecievable(accountNameRecievable);
    setChartOfAccountNamePayable(accountNamePayable);
    setShowChartOfAccountSuccessfull(true);
    setShowConnect(false);
  };

  const handleTabChange = (value: string) => {
    if (value === 'open' || value === 'closed') {
      setActiveTab(value);
    }
  };

  return (
    <AccountingSystemContext.Provider value={accountingSystemCtx}>
      {isInitialized && (
        <>
          {!showConnect && (
            <>
              <Box flx="row" jc="sb" ai="fs">
                <Box flx="col">
                  <Title title={titleMap[Type]} />

                  <Box flx="col" width="100%" mt="xmd" mb="xmd" base="8">
                    <p>{t('pageSubTitle')}</p>
                  </Box>
                </Box>
                <Box>
                  <AvailableBalance availableBalance={balanceResponse.AvailableFunds} />
                </Box>
              </Box>
              <Box jc="sb" ai="center">
                <Box ai="center" gap="sm">
                  <Box gap="sm">
                    {(syncStatusResponse?.SyncStatus === 'success' || syncStatusResponse?.SyncStatus === 'empty') && (
                      <Box>{t('lastSync', { lastSync })}</Box>
                    )}
                    {syncStatusResponse?.SyncStatus === 'pending' && (
                      <Box>
                        <Trans
                          ns="translations"
                          i18nKey="AccountingSystem.SyncStatus.label"
                          components={{
                            span: <span className={styles.syncHighlight} />,
                          }}
                        />
                        <div className={styles.tooltipContianer}>
                          <Tooltip icon="question" information={t('SyncStatus.tooltip')} position="topLeft" />
                        </div>
                      </Box>
                    )}
                    {syncStatusResponse?.SyncStatus === 'failed' && (
                      <Box>
                        <div className={styles.tooltipContianerError}>
                          <Tooltip
                            customElement={<Icon icon="warning" color="error" height="17px" width="17px" />}
                            hasButton={false}
                            position="top"
                            information={t('SyncStatusFail.tooltip')}
                          />
                        </div>
                        <Trans
                          ns="translations"
                          i18nKey="AccountingSystem.SyncStatusFail.label"
                          values={{ lastSync }}
                          components={{
                            span: <span className={[styles.syncHighlight, styles.error].join(' ')} />,
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                  <Button
                    id="Sync-Data"
                    color="secondary"
                    label=""
                    isDisabled={isSyncingData}
                    type="button"
                    icon="sync"
                    height="36px"
                    width="36px"
                    iconLocation="left"
                    iconHeight="14px"
                    iconWidth="14px"
                    round
                    onClick={() => {
                      if (!isSyncingData && businessAccount) {
                        setIsSyncingData(true);
                        syncData({
                          BusinessName: businessAccount.BusinessName || '',
                          ServiceName: businessAccount.ServiceName || '',
                        });
                      }
                    }}
                  />
                </Box>
              </Box>
              <Box flx="col" width="100%" mt="lg">
                <CardTabs.Root value={activeTab} onValueChange={handleTabChange}>
                  <CardTabs.List aria-label="Accounting Software">
                    <CardTabs.Trigger value="open">{t('openTab', { Type })}</CardTabs.Trigger>
                    <CardTabs.Trigger value="closed">{t('closedTab', { Type })}</CardTabs.Trigger>
                  </CardTabs.List>

                  <CardTabs.Content value="open">
                    <Box flx="col" gap="sm">
                      <TableFiltersContainer filtersApplied>
                        <TableFiltersContainer.Trigger />
                        <TableFiltersContainer.Content>
                          <AccountingSystemSearchForm
                            section={Type}
                            country={response.Country}
                            type={type}
                            timeRange={timeRange}
                            startDate={startDate}
                            endDate={endDate}
                            currency={currency}
                            searchTerm={searchTerm}
                            handleSearchTermChange={setSearchTerm}
                            handleCurrencyChange={setCurrency}
                            handleTypeChange={setType}
                            handleTimeRangeChange={(newTimeRange) => {
                              if (newTimeRange !== timeRange) {
                                timeRangeChange(newTimeRange);
                              }
                            }}
                            handleDateRangeChange={(dateRange) => {
                              if (dateRange[0] !== startDate || dateRange[1] !== endDate) {
                                setStartDate(dateRange[0]);
                                setEndDate(dateRange[1]);
                                setTimeRange('custom');
                              }
                            }}
                            getTransactionLists={() => {
                              if (!businessAccount) return;
                              if (lastStartDate === startDate && lastEndDate === endDate) {
                                searchTransactions();
                              } else {
                                fetchBillsOrInvoices(Type, businessAccount);
                              }
                              setLastStartDate(startDate);
                              setLastEndDate(endDate);
                            }}
                          />
                        </TableFiltersContainer.Content>
                      </TableFiltersContainer>
                    </Box>
                    <AccountingSystemOpen
                      OnSuccess={OnSuccess}
                      BillsOrInvoices={railzBillsOrInvoicesFiltered}
                      BusinessName={businessAccount?.BusinessName || ''}
                      ServiceName={businessAccount?.ServiceName || ''}
                      Type={Type}
                      AccountBalance={balanceResponse}
                    />
                  </CardTabs.Content>

                  <CardTabs.Content value="closed">
                    <Box flx="col" gap="sm">
                      <TableFiltersContainer filtersApplied>
                        <TableFiltersContainer.Trigger />
                        <TableFiltersContainer.Content>
                          <AccountingSystemSearchForm
                            section={Type}
                            country={response.Country}
                            type={type}
                            timeRange={timeRange}
                            startDate={startDate}
                            endDate={endDate}
                            currency={currency}
                            searchTerm={searchTerm}
                            handleSearchTermChange={setSearchTerm}
                            handleCurrencyChange={setCurrency}
                            handleTypeChange={setType}
                            handleTimeRangeChange={(newTimeRange) => {
                              if (newTimeRange !== timeRange) {
                                timeRangeChange(newTimeRange);
                              }
                            }}
                            handleDateRangeChange={(dateRange) => {
                              if (dateRange[0] !== startDate || dateRange[1] !== endDate) {
                                setStartDate(dateRange[0]);
                                setEndDate(dateRange[1]);
                                setTimeRange('custom');
                              }
                            }}
                            getTransactionLists={() => {
                              if (!businessAccount) return;
                              if (lastStartDate === startDate && lastEndDate === endDate) {
                                searchTransactions();
                              } else {
                                fetchBillsOrInvoices(Type, businessAccount);
                              }
                              setLastStartDate(startDate);
                              setLastEndDate(endDate);
                            }}
                          />
                        </TableFiltersContainer.Content>
                      </TableFiltersContainer>
                    </Box>
                    <AccountingSystemClosed
                      ServiceName={businessAccount?.ServiceName || ''}
                      Type={Type}
                      BillsOrInvoices={BillsOrInvoicesFiltered}
                    />
                  </CardTabs.Content>
                </CardTabs.Root>
              </Box>
              <Toast
                label={
                  <Trans
                    ns="translations"
                    i18nKey="AccountingSystem.showChartOfAccountSuccessfulToast"
                    values={{ chartOfAccountNameRecievable, chartOfAccountNamePayable }}
                    components={{
                      span: <span style={{ fontWeight: 700 }} />,
                    }}
                  />
                }
                isVisible={showChartOfAccountSuccessfull}
              />
            </>
          )}
          {showConnect && (
            <AccountingSoftware redirectURL={RedirectURL} onCompleteChartOfAccount={onCompleteChartOfAccount} />
          )}
        </>
      )}
      <AccountingSystemDialogue handleDialogue={handlers.handleAccountDialogue} />
    </AccountingSystemContext.Provider>
  );
};
export default AccountingSystemContainer;
